/**
 * Client-side "biggest discount" sorting (sort_by=discount-descending).
 *
 * Shopify only accepts a fixed set of sort_by values, so this custom value is
 * resolved on the client: when the URL contains sort_by=discount-descending,
 * every page of the current collection (with the active filters preserved) is
 * fetched through the Section Rendering API, the cards are sorted by the
 * data-discount-percent attribute computed in Liquid and the grid content is
 * replaced. Available products stay ahead of sold-out ones, matching the
 * server-side rendering of main-collection-product-grid.
 */
class DiscountSort {
  constructor() {
    this.sortValue = 'discount-descending';
    // Valid value sent to the server on page fetches, so pagination stays deterministic.
    this.fetchSortValue = 'price-descending';
    this.batchSize = 4;
    this.applying = false;

    this.syncSelects();
    this.observeGridRenders();
    if (this.isActive()) this.apply();
  }

  isActive() {
    return new URLSearchParams(window.location.search).get('sort_by') === this.sortValue;
  }

  getGrid() {
    const grid = document.getElementById('product-grid');
    return grid && grid.tagName === 'UL' ? grid : null;
  }

  // The server does not know the custom value, so (re-)rendered selects can
  // come back with the default option selected.
  syncSelects() {
    if (!this.isActive()) return;
    document.querySelectorAll('select[name="sort_by"]').forEach((select) => {
      if (select.querySelector(`option[value="${this.sortValue}"]`)) select.value = this.sortValue;
    });
  }

  // facets.js replaces #ProductGridContainer's content on its AJAX paths
  // (mobile filter checkboxes, facet remove pills, popstate); re-apply afterwards.
  observeGridRenders() {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    new MutationObserver(() => {
      if (this.applying || !this.isActive()) return;
      this.syncSelects();
      this.apply();
    }).observe(container, { childList: true });
  }

  async apply() {
    const grid = this.getGrid();
    if (!grid || this.applying) return;
    this.applying = true;
    this.toggleLoading(true);
    try {
      const items = document.querySelector('#ProductGridContainer .pagination-wrapper')
        ? await this.fetchAllItems(grid)
        : Array.from(grid.querySelectorAll(':scope > .grid__item'));
      this.render(grid, items);
    } catch (error) {
      console.warn('[discount-sort] Não foi possível ordenar por maior desconto:', error);
    } finally {
      this.toggleLoading(false);
      this.applying = false;
    }
  }

  async fetchAllItems(grid) {
    const maxPages = parseInt(grid.dataset.discountSortMaxPages, 10) || 20;
    const params = new URLSearchParams(window.location.search);
    params.set('section_id', grid.dataset.id);
    params.set('sort_by', this.fetchSortValue);
    params.delete('page');
    const baseUrl = `${window.location.pathname}?${params.toString()}`;

    const items = [];
    let page = 1;
    let lastPageReached = false;
    while (!lastPageReached && page <= maxPages) {
      const requests = [];
      while (requests.length < this.batchSize && page <= maxPages) {
        requests.push(this.fetchPageItems(`${baseUrl}&page=${page}`));
        page += 1;
      }
      for (const pageItems of await Promise.all(requests)) {
        if (pageItems.length === 0) {
          lastPageReached = true;
          break;
        }
        items.push(...pageItems);
      }
    }
    if (!lastPageReached) {
      console.warn(
        `[discount-sort] Coleção com mais de ${maxPages} páginas; ordenando apenas os produtos carregados.`
      );
    }
    return items;
  }

  async fetchPageItems(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status} (${url})`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = doc.getElementById('product-grid');
    // Pages past the last one render the empty state (a div), not the grid ul.
    if (!grid || grid.tagName !== 'UL') return [];
    return Array.from(grid.querySelectorAll(':scope > .grid__item'));
  }

  render(grid, items) {
    const seen = new Set();
    const sorted = items
      .filter((item) => {
        const id = item.dataset.productId;
        if (!id) return true;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .map((item, index) => ({ item, index }))
      .sort((a, b) => this.compare(a, b))
      .map(({ item }) => item);

    sorted.forEach((item) => {
      if (item.classList.contains('scroll-trigger')) item.classList.add('scroll-trigger--cancel');
    });
    grid.replaceChildren(...sorted);

    // The full result set is rendered at once, so pagination no longer applies.
    const pagination = document.querySelector('#ProductGridContainer .pagination-wrapper');
    if (pagination) pagination.remove();
  }

  compare(a, b) {
    const soldOut = (entry) => (entry.item.dataset.available === 'false' ? 1 : 0);
    const discount = (entry) => parseFloat(entry.item.dataset.discountPercent) || 0;
    return soldOut(a) - soldOut(b) || discount(b) - discount(a) || a.index - b.index;
  }

  toggleLoading(on) {
    const container = document.getElementById('ProductGridContainer');
    const collection = container ? container.querySelector('.collection') : null;
    if (collection) collection.classList.toggle('loading', on);
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.toggle('hidden', !on));
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new DiscountSort());
} else {
  new DiscountSort();
}
